import { Capacitor } from "@capacitor/core";
import { useState } from "react";

import {
  CURRENT_ORDER_QUERY_KEY,
  invalidateCurrentOrderDetails,
} from "@/modules/cart/useCartDetails";
import { LocalStorageService } from "@/modules/storage";
import { del } from "api";
import { components } from "api/generated";
import { ShopDisplayType } from "api/types";
import { queryClient } from "pages/Root";

import { PROMOTION_MODAL_SEEN_KEY } from "../constants";

export const setPromotionModalSeen = (seen: boolean) => {
  if (seen) {
    LocalStorageService.setItem(PROMOTION_MODAL_SEEN_KEY, "true");
  } else {
    LocalStorageService.removeItem(PROMOTION_MODAL_SEEN_KEY);
  }
};

export const getPromotionModalSeen = () => {
  return LocalStorageService.getItem(PROMOTION_MODAL_SEEN_KEY) === "true";
};

export const removeArchiveItems = async (
  details: components["schemas"]["ShopDisplayOrderResult"]
) => {
  let itemsArchived = 0;

  if (details?.shopDisplayOrderLineItems?.length) {
    const archivedItems = details.shopDisplayOrderLineItems.filter(
      (item) => !item.active
    );
    if (archivedItems.length) {
      itemsArchived = archivedItems.length;
      const results = await Promise.allSettled(
        archivedItems.map(
          async (archiveItem) =>
            await del(
              `/orders/current/lineitems/{shopDisplayOrderLineItemId}`,
              {
                shopDisplayOrderLineItemId: archiveItem.id,
              }
            )
        )
      );

      await invalidateCurrentOrderDetails();

      const isAnyFailure = results.some(({ status }) => status === "rejected");
      if (isAnyFailure) {
        throw new Error("Some items failed to be removed.");
      }
    }
  }

  return itemsArchived;
};

export const buildPlatformUrl = (
  scheme: string | null | undefined,
  subdomain: string | null | undefined,
  host: string | null | undefined,
  page?: string | null | undefined
) => {
  if (page == "checkout" && Capacitor.isNativePlatform()) {
    return `${window.location.origin}`;
  }
  const uri = (host ?? "").includes(subdomain ?? "")
    ? host
    : `${subdomain}.${host}`;
  return `${scheme}://${uri}`;
};

export const getAdminUrl = () =>
  window.location.protocol +
  "//" +
  window.location.host
    .replace(/[^(\d+.|.)]*\.?/, "admin.")
    .replace(/\w+\.com/, "repeatmd.app");

export const getPDPUrl = (listingType: number, listingId: string) => {
  if (listingType === ShopDisplayType.Membership) {
    return `/pdp/membership/${listingId}`;
  } else if (listingType === ShopDisplayType.GiftCard) {
    return `/shop/gift-cards`;
  } else if (
    listingType === ShopDisplayType.Product ||
    listingType === ShopDisplayType.ProductFromExternalSource
  ) {
    return `/pdp/product/${listingId}`;
  } else {
    return `/pdp/package/${listingId}`;
  }
};

export function useStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = LocalStorageService.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (err) {
      console.error(`Error reading storage key "${key}":`, err);
      return initialValue;
    }
  });

  const setValue: React.Dispatch<React.SetStateAction<T>> = (value) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;

      setStoredValue(valueToStore);

      if (valueToStore === null || valueToStore === undefined) {
        LocalStorageService.removeItem(key);
      } else {
        LocalStorageService.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (err) {
      console.error(`Error writing storage key "${key}":`, err);
    }
  };

  return [storedValue, setValue] as const;
}

export const calculateRepeatCashEligibleAmount = (
  cartDetails: components["schemas"]["ShopDisplayOrderResult"]
): number => {
  if (!cartDetails.shopDisplayOrderLineItems) {
    return 0;
  }

  const totalItemsAmount = cartDetails.shopDisplayOrderLineItems
    .filter(({ displayType }) => displayType !== ShopDisplayType.GiftCard)
    .reduce((total, { cost }) => total + cost, 0);

  return totalItemsAmount;
};

export const getPossessiveForm = (name?: string | null): string => {
  if (!name) {
    return "";
  }

  return name.endsWith("s") ? `${name}'` : `${name}'s`;
};
